import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase";
import Navbar from "../components/Navbar";
import { convertToWebP } from "../utils/imageConverter";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "../css/ManageHeroSlides.css";
import toast from "react-hot-toast";

// Sortable Slide Card Component
function SortableSlideCard({ slide, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mhs-slide-card ${isDragging ? "dragging" : ""}`}
    >
      <div className="mhs-drag-handle" {...attributes} {...listeners}>
        <i className="fas fa-grip-vertical"></i>
      </div>
      <div className="mhs-slide-image">
        {slide.imageUrl ? (
          <img src={slide.imageUrl} alt={slide.title} loading="lazy" />
        ) : (
          <div className="mhs-no-image">لا توجد صورة</div>
        )}
        {!slide.isActive && <div className="mhs-inactive-badge">غير نشط</div>}
      </div>
      <div className="mhs-slide-content">
        <h3>{slide.title || "بدون عنوان"}</h3>
        <p>{slide.subtitle || "بدون نص فرعي"}</p>
        <div className="mhs-slide-meta">
          {slide.buttonText && (
            <span className="mhs-button-text">زر: {slide.buttonText}</span>
          )}
        </div>
        <div className="mhs-slide-actions">
          <button className="mhs-btn edit" onClick={() => onEdit(slide)}>
            <i className="fas fa-edit"></i> تعديل
          </button>
          <button className="mhs-btn delete" onClick={() => onDelete(slide)}>
            <i className="fas fa-trash-alt"></i> حذف
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageHeroSlides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    buttonText: "",
    buttonLink: "/",
    isActive: true,
    textColor: "#FFFFFF", // Default white color
    buttonColor: "#BFA57A", // Default button color
  });

  const [colorErrors, setColorErrors] = useState({
    textColor: "",
    buttonColor: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Drag and drop sensors with mobile support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms hold before drag starts on mobile
        tolerance: 5, // 5px tolerance for movement during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Available pages for customers
  const availablePages = [
    { value: "/", label: "الصفحة الرئيسية" },
    { value: "/products", label: "المنتجات" },
    { value: "/about", label: "من نحن" },
    { value: "/contact", label: "اتصل بنا" },
    { value: "/cart", label: "سلة المشتريات" },
  ];

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "heroSlides"));
      const slidesData = [];
      querySnapshot.forEach((doc) => {
        slidesData.push({ id: doc.id, ...doc.data() });
      });
      // Sort by order
      slidesData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setSlides(slidesData);
    } catch (error) {
      toast.error("حدث خطأ أثناء تحميل الشرائح");
    } finally {
      setLoading(false);
    }
  };

  const isValidHexColor = (color) => {
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  };

  const handleColorChange = (name, value) => {
    // Allow empty input while typing
    if (value === "" || value === "#") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setColorErrors((prev) => ({
        ...prev,
        [name]: "أدخل لون صالح بصيغة #RRGGBB",
      }));
      return;
    }

    // Add # if missing
    if (!value.startsWith("#")) {
      value = "#" + value;
    }

    // Update the form data
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate complete color
    if (value.length === 7) {
      if (isValidHexColor(value)) {
        setColorErrors((prev) => ({ ...prev, [name]: "" }));
      } else {
        setColorErrors((prev) => ({
          ...prev,
          [name]: "لون غير صالح. استخدم الصيغة #RRGGBB",
        }));
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "textColor" || name === "buttonColor") {
      handleColorChange(name, value);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file) => {
    // Convert to WebP
    let fileToUpload;
    try {
      fileToUpload = await convertToWebP(file);
    } catch (error) {
      console.error("Error converting image to WebP:", error);
      fileToUpload = file; // Fallback to original file if conversion fails
    }

    const timestamp = Date.now();
    const imageName = `hero-${timestamp}-${fileToUpload.name}`;
    const storageRef = ref(storage, `heroSlides/${imageName}`);
    await uploadBytes(storageRef, fileToUpload);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate colors before submission
    if (!isValidHexColor(formData.textColor)) {
      toast.error("لون النص غير صالح. يجب أن يكون بصيغة #RRGGBB");
      return;
    }

    if (!isValidHexColor(formData.buttonColor)) {
      toast.error("لون الزر غير صالح. يجب أن يكون بصيغة #RRGGBB");
      return;
    }

    setUploading(true);

    try {
      let imageUrl = editingSlide?.imageUrl || "";

      // Upload new image if selected
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const slideData = {
        ...formData,
        imageUrl,
        updatedAt: new Date(),
      };

      if (editingSlide) {
        // Update existing slide (keep existing order)
        slideData.order = editingSlide.order;
        await updateDoc(doc(db, "heroSlides", editingSlide.id), slideData);
        toast.success("تم تحديث الشريحة بنجاح");
      } else {
        // Add new slide at the end
        slideData.order = slides.length;
        slideData.createdAt = new Date();
        await addDoc(collection(db, "heroSlides"), slideData);
        toast.success("تم إضافة الشريحة بنجاح");
      }

      fetchSlides();
      closeModal();
    } catch (error) {
      toast.error("حدث خطأ أثناء حفظ الشريحة");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormData({
      title: slide.title || "",
      subtitle: slide.subtitle || "",
      buttonText: slide.buttonText || "",
      buttonLink: slide.buttonLink || "",
      isActive: slide.isActive !== false,
      textColor: slide.textColor || "#FFFFFF",
      buttonColor: slide.buttonColor || "#BFA57A",
    });
    setImagePreview(slide.imageUrl || null);
    setShowModal(true);
  };

  const handleDelete = async (slide) => {
    const confirmDelete = await new Promise((resolve) => {
      const toastId = toast(
        (t) => (
          <div style={{ textAlign: "center" }}>
            <p style={{ marginBottom: "15px", fontWeight: "bold" }}>
              هل أنت متأكد من حذف هذه الشريحة؟
            </p>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  resolve(true);
                }}
                style={{
                  padding: "8px 20px",
                  background: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                حذف
              </button>
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  resolve(false);
                }}
                style={{
                  padding: "8px 20px",
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                إلغاء
              </button>
            </div>
          </div>
        ),
        { duration: Infinity },
      );
    });
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "heroSlides", slide.id));
      toast.success("تم حذف الشريحة بنجاح");
      fetchSlides();
    } catch (error) {
      toast.error("حدث خطأ أثناء حذف الشريحة");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSlide(null);
    setFormData({
      title: "",
      subtitle: "",
      buttonText: "",
      buttonLink: "/",
      isActive: true,
      textColor: "#FFFFFF",
      buttonColor: "#BFA57A",
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = slides.findIndex((slide) => slide.id === active.id);
    const newIndex = slides.findIndex((slide) => slide.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder slides array
    const newSlides = arrayMove(slides, oldIndex, newIndex);

    // Update local state immediately for smooth UI
    setSlides(newSlides);

    // Update Firestore with new order
    try {
      const batch = writeBatch(db);
      newSlides.forEach((slide, index) => {
        const slideRef = doc(db, "heroSlides", slide.id);
        batch.update(slideRef, { order: index });
      });
      await batch.commit();
      toast.success("تم تحديث ترتيب الشرائح بنجاح");
    } catch (error) {
      console.error("Error updating slide order:", error);
      toast.error("حدث خطأ أثناء تحديث الترتيب");
      // Revert to original order on error
      fetchSlides();
    }
  };

  return (
    <>
      <Navbar />
      <div className="manage-hero-slides">
        <div className="mhs-header">
          <h1>إدارة شرائح الصفحة الرئيسية</h1>
          <button
            className="mhs-btn primary"
            onClick={() => setShowModal(true)}
          >
            + إضافة شريحة جديدة
          </button>
        </div>

        {loading ? (
          <div className="mhs-loading">
            <div className="spinner"></div>
            <p>جاري التحميل...</p>
          </div>
        ) : (
          <>
            <div className="mhs-drag-info">
              <i className="fas fa-info-circle"></i>
              <span>اسحب وأسقط الشرائح لإعادة ترتيبها</span>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={slides.map((slide) => slide.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="mhs-slides-grid">
                  {slides.map((slide) => (
                    <SortableSlideCard
                      key={slide.id}
                      slide={slide}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                  {slides.length === 0 && (
                    <div className="mhs-empty">
                      <p>لا توجد شرائح. قم بإضافة شريحة جديدة للبدء.</p>
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </>
        )}

        {showModal && (
          <div className="mhs-modal-overlay" onClick={closeModal}>
            <div className="mhs-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mhs-modal-header">
                <h2>{editingSlide ? "تعديل الشريحة" : "إضافة شريحة جديدة"}</h2>
                <button className="mhs-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={handleSubmit} className="mhs-form">
                <div className="mhs-form-group">
                  <label>العنوان الرئيسي *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="مثال: أحدث منتجات العناية بالشعر"
                  />
                </div>

                <div className="mhs-form-group">
                  <label>النص الفرعي</label>
                  <textarea
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    placeholder="مثال: اكتشف مجموعتنا المميزة من منتجات العناية"
                    rows="3"
                  />
                </div>

                <div className="mhs-form-row">
                  <div className="mhs-form-group">
                    <label>نص الزر</label>
                    <input
                      type="text"
                      name="buttonText"
                      value={formData.buttonText}
                      onChange={handleInputChange}
                      placeholder="مثال: تسوق الآن"
                    />
                  </div>

                  <div className="mhs-form-group">
                    <label>رابط الزر</label>
                    <select
                      name="buttonLink"
                      value={formData.buttonLink}
                      onChange={handleInputChange}
                      className="mhs-select"
                    >
                      {availablePages.map((page) => (
                        <option key={page.value} value={page.value}>
                          {page.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mhs-form-row">
                  <div className="mhs-form-group">
                    <label>إظهار الشريحة</label>
                    <label className="mhs-checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span>نشط</span>
                    </label>
                  </div>

                  <div className="mhs-form-group">
                    <label>لون النص</label>
                    <div className="color-picker-container">
                      <input
                        type="color"
                        name="textColor"
                        value={
                          isValidHexColor(formData.textColor)
                            ? formData.textColor
                            : "#FFFFFF"
                        }
                        onChange={handleInputChange}
                        className="mhs-color-picker"
                      />
                      <input
                        type="text"
                        name="textColor"
                        value={formData.textColor}
                        onChange={handleInputChange}
                        placeholder="#FFFFFF"
                        className={`mhs-color-input ${colorErrors.textColor ? "error" : ""}`}
                      />
                    </div>
                    {colorErrors.textColor && (
                      <div className="mhs-color-error">
                        {colorErrors.textColor}
                      </div>
                    )}
                  </div>

                  <div className="mhs-form-group">
                    <label>لون الزر</label>
                    <div className="color-picker-container">
                      <input
                        type="color"
                        name="buttonColor"
                        value={
                          isValidHexColor(formData.buttonColor)
                            ? formData.buttonColor
                            : "#BFA57A"
                        }
                        onChange={handleInputChange}
                        className="mhs-color-picker"
                      />
                      <input
                        type="text"
                        name="buttonColor"
                        value={formData.buttonColor}
                        onChange={handleInputChange}
                        placeholder="#BFA57A"
                        className={`mhs-color-input ${colorErrors.buttonColor ? "error" : ""}`}
                      />
                    </div>
                    {colorErrors.buttonColor && (
                      <div className="mhs-color-error">
                        {colorErrors.buttonColor}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mhs-form-group">
                  <label>صورة الشريحة *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    required={!editingSlide}
                  />
                  {imagePreview && (
                    <div className="mhs-image-preview">
                      <img src={imagePreview} alt="Preview" />
                    </div>
                  )}
                </div>

                <div className="mhs-form-actions">
                  <button
                    type="button"
                    className="mhs-btn cancel"
                    onClick={closeModal}
                    disabled={uploading}
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="mhs-btn primary"
                    disabled={uploading}
                  >
                    {uploading
                      ? "جاري الحفظ..."
                      : editingSlide
                        ? "تحديث الشريحة"
                        : "إضافة الشريحة"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ManageHeroSlides;
